import { useContext, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Layout, Menu, MenuProps } from "antd";
import {
  DashboardOutlined,
  UserOutlined,
  GoldOutlined,
  ReconciliationOutlined,
  HeartOutlined,
  ToolOutlined,
  MailOutlined,
  MessageOutlined,
} from "@ant-design/icons";

import "../../styles/SideBar.css";
import { useGuardedNavigate } from "../../hooks/useGuardedNavigate";
import { AuthContext } from "../../context/AuthContext";

const { Sider } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: any
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
  } as MenuItem;
}

const AdminSideBar: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useGuardedNavigate();
  const location = useLocation();
  const { currentUser } = useContext(AuthContext);
  const isSuperAdmin = currentUser?.Role?.Name === "Super Admin";

  const selectedKey = location.pathname.startsWith("/admin/maintenance")
    ? "/admin/maintenance"
    : location.pathname.startsWith("/admin/email-queue")
      ? "/admin/email-queue"
      : location.pathname.startsWith("/admin/sms-queue")
        ? "/admin/sms-queue"
        : location.pathname;

  const items: MenuProps["items"] = useMemo(
    () => [
      getItem("Dashboard", "/admin/dashboard", <DashboardOutlined />),
      getItem("Users", "/admin/users", <UserOutlined />),
      getItem("Groups", "/admin/groups", <GoldOutlined />),
      getItem("Reconciliation", "/admin/recon", <ReconciliationOutlined />),
      ...(isSuperAdmin
        ? [getItem("Maintenance", "/admin/maintenance", <ToolOutlined />)]
        : []),
      ...(isSuperAdmin
        ? [getItem("Email Queue", "/admin/email-queue", <MailOutlined />)]
        : []),
      ...(isSuperAdmin
        ? [getItem("SMS Queue", "/admin/sms-queue", <MessageOutlined />)]
        : []),
      getItem("Cron Health", "/health-dashboard", <HeartOutlined />),
    ],
    [isSuperAdmin]
  );

  const onClick: MenuProps["onClick"] = (e) => {
    void navigate(e.key);
  };

  const siderWidth = 200;
  const collapsedWidth = window.innerWidth < 768 ? 0 : 80;
  const spacerWidth = collapsed ? collapsedWidth : siderWidth;

  return (
    <>
      <div
        style={{
          width: spacerWidth,
          flexShrink: 0,
          transition: "width 0.2s",
        }}
      />
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        breakpoint="lg"
        collapsedWidth={collapsedWidth}
        width={siderWidth}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          height: "100vh",
          overflow: "auto",
          zIndex: 10,
        }}
      >
        <div className="space" />
        <Menu
          onClick={onClick}
          selectedKeys={[selectedKey]}
          theme="light"
          mode="inline"
          items={items}
        />
      </Sider>
    </>
  );
};

export default AdminSideBar;
